import { isCioColorFacet } from './filters';
import {
  buildImageServiceUrlFromItemNumber,
  buildSwatchImageUrlFromSku,
} from './images';

type FinalDetails = {
  promoCode: string;
  promoLabel: string;
  promoPrice: number;
  promoTypeId: string;
};

type DatasourceConfiguration = {
  sourceUrl: string;
  version: string;
};

type CustomerProductPrice = {
  final: number;
  finalDetails: FinalDetails;
  masterProductId: number;
  sales: number;
  vip: number;
  retail: number;
  outlet: number;
  pricingDatasource: DatasourceConfiguration;
};

type ProductImageData = {
  height: number;
  url: string;
  width: number;
};

type ProductImage = {
  angle: string;
  sizes: ProductImageData[];
};

type ProductImages = {
  default: ProductImage[];
  plus: ProductImage[];
};

type ActiveProduct = {
  id: number;
  masterProductId: number;
  label: string;
  permalink: string;
  imageServiceUrl: string;
  images: ProductImages | null;
  itemNumber: string;
  sku: string;
  pdpLink: string;
  pricing: CustomerProductPrice;
  swatchLabel: string;
  tagIdList: number[];
  membershipBrandId: string;
};

type Swatch = {
  productId: number;
  label: string;
  url: string;
  pdpLink: string;
};

/**
 * Generates a product detail page link based on the permalink and psrc.
 *
 * @param {Object} param - The parameters for generating the link.
 * @param {string} param.permalink - The permalink of the product.
 * @param {string} param.psrc - The psrc of the product.
 * @returns {string} - The generated product detail page link.
 */
const getPDPlink = ({
  permalink,
  psrc,
}: {
  permalink: string;
  psrc?: string;
}): string => {
  if (psrc) {
    const prefix = 'cio-';
    const psrcWithDedupedPrefix = `${prefix}${psrc?.replace(prefix, '')}`;
    return `/products/${permalink}?psrc=${psrcWithDedupedPrefix}`;
  }
  return `/products/${permalink}`;
};

/**
 * Data from Cio does not have a swatch label, so we need to get it from the color facet.
 * The assumption is that the first color facet option is the one we want to use and
 * matches the swatch label from related products.
 *
 * @param {Array} facets - The list of facets from the product data.
 * @returns {string} - The swatch label.
 */
const getSwatchLabelFromColorFacet = (facets: any[]): string => {
  const colorFacet = facets?.find((facet) => isCioColorFacet(facet?.name));

  // The product will typically have one (the first) color facet option associated with itself,
  // due to the nature of our product data, where we associate a separate product id for each color variation
  return colorFacet?.values?.[0];
};

/**
 * Formats the CIO product data to match the active product format.
 *
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @returns {ActiveProduct} - The formatted active product object.
 */
const formatCioProductToActiveProduct = ({
  cioProductData,
  productSource,
}: {
  cioProductData: any;
  productSource: string;
}): ActiveProduct => {
  return {
    id: Number(cioProductData?.id),
    images: null,
    imageServiceUrl: cioProductData?.image_service_url,
    itemNumber: cioProductData?.item_number,
    label: cioProductData?.label,
    masterProductId: Number(cioProductData?.id),
    membershipBrandId: cioProductData?.membership_brand_id,
    pdpLink: getPDPlink({
      permalink: cioProductData?.permalink,
      psrc: productSource,
    }),
    permalink: cioProductData?.permalink,
    pricing: cioProductData?.pricing,
    sku: cioProductData?.sku,
    swatchLabel: getSwatchLabelFromColorFacet(cioProductData?.facets),
    tagIdList:
      cioProductData?.tag_id_list?.map((tag: any) => Number(tag)) || [],
  };
};

/**
 * Formats the related product data to match the active product format.
 *
 * @param {Object} param.relatedProductData - The related product data.
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @returns {ActiveProduct} - The formatted active product object.
 */
const formatRelatedProductToActiveProduct = ({
  relatedProductData,
  cioProductData,
  productSource,
}: {
  relatedProductData: any;
  cioProductData: any;
  productSource: string;
}): ActiveProduct => {
  return {
    id: Number(relatedProductData?.masterProductId),
    images: relatedProductData?.images,
    imageServiceUrl:
      buildImageServiceUrlFromItemNumber(relatedProductData?.itemNumber) ||
      cioProductData?.image_service_url,
    itemNumber: relatedProductData?.itemNumber,
    label: cioProductData?.label,
    masterProductId: Number(relatedProductData?.masterProductId),
    membershipBrandId: relatedProductData?.membershipBrandId,
    pdpLink: getPDPlink({
      permalink: relatedProductData?.permalink,
      psrc: productSource,
    }),
    permalink: relatedProductData?.permalink,
    pricing: cioProductData?.pricing,
    sku: relatedProductData?.sku,
    swatchLabel: relatedProductData?.color,
    tagIdList:
      relatedProductData?.tags?.map((tag: { id: any }) => tag?.id) || [],
  };
};

/**
 * Get a unified formatted product object between CIO and GQL.
 *
 * @param {Array} param.relatedProducts - The list of related products.
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @param {number} param.activeProductId - The ID of the active product.
 * @param {Array} param.swatches - The list of swatches.
 * @returns {ActiveProduct} - The formatted product object.
 */
export const getActiveProduct = ({
  relatedProducts,
  cioProductData,
  productSource,
  activeProductId,
}: {
  relatedProducts: any[];
  swatches: any[];
  cioProductData: any;
  productSource: string;
  activeProductId: number;
}): ActiveProduct => {
  if (Number(activeProductId) === Number(cioProductData?.id)) {
    return formatCioProductToActiveProduct({
      cioProductData,
      productSource,
    });
  }

  const relatedProductData = relatedProducts?.find(
    (product) => Number(product.masterProductId) === Number(activeProductId)
  );

  return formatRelatedProductToActiveProduct({
    relatedProductData,
    cioProductData,
    productSource,
  });
};

/**
 * Formats the swatch data structure for a CIO product.
 *
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @param {Array} param.facets - The list of facets.
 * @returns {Swatch} - The formatted swatch object.
 */
const formatSwatchForCioProduct = ({
  cioProductData,
  productSource,
  facets,
}: {
  cioProductData: any;
  productSource: string;
  facets: any[];
}): Swatch => {
  return {
    productId: Number(cioProductData?.id),
    label: getSwatchLabelFromColorFacet(facets),
    url: buildSwatchImageUrlFromSku(cioProductData?.sku),
    pdpLink: getPDPlink({
      permalink: cioProductData?.permalink,
      psrc: productSource,
    }),
  };
};

/**
 * Formats the swatch data structure for a related product from GQL.
 *
 * @param {Object} param.swatch - The swatch object.
 * @param {string} param.productSource - The source of the product.
 * @param {Object} param.relatedProduct - The related product object.
 * @returns {Swatch} - The formatted swatch object.
 */
const formatSwatchForRelatedProduct = ({
  swatch,
  productSource,
  relatedProduct,
}: {
  swatch: any;
  productSource: string;
  relatedProduct: any;
}): Swatch => {
  return {
    productId: swatch?.productId,
    label: swatch?.label,
    url: swatch?.url,
    pdpLink: getPDPlink({
      permalink: relatedProduct?.permalink,
      psrc: productSource,
    }),
  };
};
/**
 * Generates a list of swatches with unified swatch data structure with the primary swatch first.
 * The primary swatch is the one associated with the CIO product data.
 * The related product swatches are formatted and filtered to exclude the primary swatch.
 * Related products is not guaranteed to have isSelf swatch
 * we need to add it manually and make sure it's the first one
 *
 * @param {Array} param.swatches - The list of swatches.
 * @param {string} param.productSource - The source of the product.
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {Array} param.relatedProducts - The list of related products.
 * @returns {Array} - The list of swatches with the primary swatch first.
 */
export const getSwatchesWithPdpLink = ({
  swatches,
  productSource,
  cioProductData,
  relatedProducts,
}: {
  swatches: any[];
  productSource: string;
  cioProductData: any;
  relatedProducts: any[];
}): Swatch[] => {
  const primarySwatch = formatSwatchForCioProduct({
    cioProductData,
    productSource,
    facets: cioProductData?.facets,
  });

  if (!swatches?.length || !relatedProducts?.length) {
    return [primarySwatch];
  }

  const relatedProductSwatches = swatches?.map((swatch) => {
    const relatedProduct = relatedProducts?.find(
      (product) => Number(product.masterProductId) === Number(swatch?.productId)
    );
    return formatSwatchForRelatedProduct({
      swatch,
      productSource,
      relatedProduct,
    });
  });

  // filter out swatches that are the same as the primary swatch
  // there are some instances where related products have the same productId as the primary product
  // indicated by the `swatch.isSelf` property, but this is not always the case
  // so we need to filter out the swatches that have the same productId as the primary product
  // and manually build the primary swatch to have a consistent experience
  const filteredRelatedProductSwatches = relatedProductSwatches?.filter(
    (swatch) => swatch?.productId !== primarySwatch?.productId
  );

  // concatenate all swatches
  return [primarySwatch, ...filteredRelatedProductSwatches];
};
